<div class="flex flex-col gap-3">
  <!-- Segmented Media Tabs (Vertically Centered) -->
  <div class="grid grid-cols-2 gap-1.5 p-1 bg-base-200/80 rounded-xl">
    <button
      type="button"
      class="daisy-btn daisy-btn-sm border-0 flex items-center justify-center gap-2 rounded-lg font-semibold text-xs transition-all !outline-none focus:!outline-none focus-visible:!outline-none focus:!ring-0"
      [ngClass]="activeTab === 'files' ? 'daisy-btn-primary shadow-sm' : 'daisy-btn-ghost hover:bg-base-300/50 text-base-content/70'"
      (click)="activeTab = 'files'"
    >
      <i class="fa-solid fa-folder-open text-xs"></i>
      <span class="leading-none">Files</span>
      @if ((uploadedFiles$ | async)?.length; as count) {
        <span
          class="daisy-badge daisy-badge-xs font-mono px-1.5"
          [ngClass]="activeTab === 'files' ? 'bg-primary-content/20 text-primary-content border-0' : 'daisy-badge-ghost text-base-content/60'"
        >
          {{ count }}
        </span>
      }
    </button>
    <button
      type="button"
      class="daisy-btn daisy-btn-sm border-0 flex items-center justify-center gap-2 rounded-lg font-semibold text-xs transition-all !outline-none focus:!outline-none focus-visible:!outline-none focus:!ring-0"
      [ngClass]="activeTab === 'upload' ? 'daisy-btn-primary shadow-sm' : 'daisy-btn-ghost hover:bg-base-300/50 text-base-content/70'"
      (click)="activeTab = 'upload'"
    >
      <i class="fa-solid fa-cloud-arrow-up text-xs"></i>
      <span class="leading-none">Upload</span>
    </button>
  </div>

  <!-- TAB 1: Uploaded Files List -->
  @if (activeTab === 'files') {
    <div class="space-y-3">
      <!-- Search & Refresh Header Bar -->
      <div class="flex items-center justify-between gap-2">
        <div class="relative flex-1">
          <i class="fa-solid fa-magnifying-glass absolute left-2.5 top-1/2 -translate-y-1/2 text-base-content/40 text-xs"></i>
          <input
            type="text"
            [(ngModel)]="searchQuery"
            placeholder="Search media files..."
            class="daisy-input daisy-input-bordered daisy-input-xs w-full pl-7 text-xs"
          />
        </div>
        <button
          type="button"
          class="daisy-btn daisy-btn-ghost daisy-btn-xs daisy-btn-square text-base-content/70 hover:text-primary daisy-tooltip daisy-tooltip-left"
          data-tip="Refresh media list"
          (click)="fetchCourseResources(selectedCourseId, false)"
        >
          <i class="fa-solid fa-rotate-right text-xs"></i>
        </button>
      </div>

      <!-- Scrollable Files List -->
      <div class="space-y-2 max-h-[380px] overflow-y-auto pr-1">
        @let files = (uploadedFiles$ | async) ?? [];
        @let filteredList = filterFiles(files);

        @if (filteredList.length > 0) {
          @for (item of filteredList; track item._id) {
            <div class="group flex items-center justify-between p-2.5 bg-base-100 hover:bg-base-200/60 rounded-xl border border-base-200 shadow-sm transition-all gap-2.5">
              <div class="flex items-center gap-2.5 min-w-0 flex-1">
                <div class="w-8 h-8 rounded-lg bg-base-200 flex items-center justify-center shrink-0">
                  <i [class]="getFileIcon(item.type, item.name)"></i>
                </div>
                <div class="min-w-0 flex-1">
                  <div class="text-xs font-semibold text-base-content truncate group-hover:text-primary transition-colors" [title]="item.name">
                    {{ item.name }}
                  </div>
                  <div class="flex items-center gap-1.5 mt-0.5">
                    @if (item.uploadStatus === 'UPLOADING' || item.uploadStatus === 'PROCESSING') {
                      <span class="inline-flex items-center gap-1 text-[10px] text-warning font-medium">
                        <span class="daisy-loading daisy-loading-spinner daisy-loading-xs"></span> Processing
                      </span>
                    } @else if (item.uploadStatus === 'COMPLETED') {
                      <span class="daisy-badge daisy-badge-success daisy-badge-xs text-white text-[9px] font-bold">Ready</span>
                    } @else if (item.uploadStatus === 'FAILED') {
                      <span class="daisy-badge daisy-badge-error daisy-badge-xs text-white text-[9px] font-bold">Failed</span>
                    } @else {
                      <span class="daisy-badge daisy-badge-ghost daisy-badge-xs text-[9px]">{{ item.uploadStatus }}</span>
                    }
                    @if (item.type) {
                      <span class="text-[10px] text-base-content/40 uppercase font-mono">{{ item.type }}</span>
                    }
                  </div>
                </div>
              </div>

              <!-- Action: Copy Resource ID -->
              <button
                type="button"
                class="daisy-btn daisy-btn-ghost daisy-btn-xs daisy-btn-square opacity-60 hover:opacity-100 daisy-tooltip daisy-tooltip-left"
                data-tip="Copy resource ID"
                (click)="copyResourceId(item._id)"
              >
                <i class="fa-solid fa-copy text-xs"></i>
              </button>
            </div>
          }
        } @else {
          <!-- Empty State -->
          <div class="text-center py-8 px-4 bg-base-100 rounded-xl border border-dashed border-base-300">
            <div class="w-10 h-10 rounded-full bg-primary/10 text-primary flex items-center justify-center mx-auto mb-2 text-base">
              <i class="fa-solid fa-folder-open"></i>
            </div>
            <p class="text-xs font-semibold text-base-content mb-1">
              {{ searchQuery ? 'No files match your search' : 'No media files uploaded' }}
            </p>
            <p class="text-[11px] text-base-content/60 max-w-xs mx-auto mb-3 leading-relaxed">
              {{ searchQuery ? 'Try clearing your search query.' : 'Upload videos or documents to attach to your curriculum lessons.' }}
            </p>
            @if (!searchQuery) {
              <button
                type="button"
                class="daisy-btn daisy-btn-primary daisy-btn-xs gap-1"
                (click)="activeTab = 'upload'"
              >
                <i class="fa-solid fa-plus text-[10px]"></i>
                <span>Upload Media</span>
              </button>
            }
          </div>
        }
      </div>
    </div>
  }

  <!-- TAB 2: Upload Files -->
  @if (activeTab === 'upload') {
    <div class="space-y-3">
      <div class="p-2 bg-base-200/50 rounded-xl text-center">
        <h4 class="text-xs font-bold text-base-content uppercase tracking-wider mb-0.5">Upload Files & Docs</h4>
        <p class="text-[11px] text-base-content/60">
          Upload video lectures (MP4, MKV) or documents (PDF, DOCX) in bulk.
        </p>
      </div>

      <div class="bg-base-100 rounded-xl border border-base-200 p-2 shadow-sm">
        <black-multi-file-upload
          [selectedCourseId]="selectedCourseId$ | async"
        ></black-multi-file-upload>
      </div>
    </div>
  }
</div>
